import { escapeRegExp } from "lodash";
import type { IFamily, IFamilyFilters } from "./types";

/** Returns all unique color temperature values (in K) across the given families. */
export function getUniqueColors(families: IFamily[]): number[] {
  return Array.from(new Set(families.flatMap((f) => f.colors ?? [])));
}

/** Returns all unique luminous flux values (in lm) across the given families. */
export function getUniqueFlux(families: IFamily[]): number[] {
  return Array.from(new Set(families.flatMap((f) => f.lightfluxes ?? [])));
}

/** Returns all unique optic (light distribution) strings across the given families. */
export function getUniqueOptics(families: IFamily[]): string[] {
  return Array.from(new Set(families.flatMap((f) => f.optics ?? [])));
}

/**
 * Sorts and filters a list of families against the current search term and active filters.
 *
 * Filtering rules (all must pass):
 * - **search**  — family name matches `searchValue` (case-insensitive)
 * - **color**   — family has at least one color in `filters.color` (skipped when filter is empty)
 * - **optic**   — family has at least one optic in `filters.optic` (skipped when filter is empty)
 * - **flux**    — family has at least one flux value within `[fluxMin, fluxMax]` (skipped when fewer than 2 flux options exist)
 *
 * @param families   Full list of families to filter.
 * @param searchValue Current search input string.
 * @param filters    Active filter selections for color, flux range, and optics.
 * @param fluxArr    All unique flux values — used to decide whether flux filtering is applicable.
 * @returns Alphabetically sorted, filtered list of families.
 */
export function filterFamilies(families: IFamily[], searchValue: string, filters: IFamilyFilters, fluxArr: number[]): IFamily[] {
  const [fluxMin, fluxMax] = filters.flux;

  return [...families]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((family) => new RegExp(escapeRegExp(searchValue), "i").exec(family.name))
    .filter((family) => filters.color.length === 0 || family.colors.some((color) => filters.color.includes(color)))
    .filter((family) => filters.optic.length === 0 || family.optics.some((optic) => filters.optic.includes(optic)))
    .filter((family) => fluxArr.length < 2 || family.lightfluxes.some((flux) => flux >= fluxMin && flux <= fluxMax));
}
